import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import Button from "../../../../../../components/atoms/button";
import Input from "../../../../../../components/atoms/input/input";

import {
  RootState,
  useAppDispatch,
  useAppSelector
} from "../../../../../../store/store";
import { AssessmentSliceActions } from "@assessment/store/slices/assessmentSlice";
import {
  useStartAssessmentMutation,
  useUpdateSessionIdMutation
} from "@assessment/store/slices/api/assessmentApiSlice";

import { ProctoringFunctionsContext } from "../../../../../../contexts/proctoringFunctionsContext";

import { requestFullScreen } from "../../../../../../utils/global/fullScreenFunctions";
import { useSelector } from "react-redux";
import { useShowErrors } from "../../../toasterWrapper";
import { generateUniqueId } from "../generateId";

interface Props {
  setActiveStep: (val: "next" | "previous") => void;
}

interface FormValues {
  email: string;
  firstName?: string | null;
  lastName?: string | null;
}

const validations = yup.object().shape({
  email: yup
    .string()
    .email("Email must be a valid.")
    .required("Email is required."),
  firstName: yup.string().notRequired(),
  lastName: yup.string().notRequired()
});

const PersonalDetails = ({ setActiveStep }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const showErrors = useShowErrors();

  const [userInternalDetails, setUserInternalDetails] = useState(false);

  const { scheduleId } = useParams();

  const [startAssessment] = useStartAssessmentMutation();
  const [updateTabId] = useUpdateSessionIdMutation();

  const context = useContext(ProctoringFunctionsContext);
  if (!context) {
    throw new Error("Context Error");
  }

  const { startProctoring } = context;
  const candidateDetails = useAppSelector(
    (state) => state.AssessmentSlice.candidateDetails
  );

  const ongoingId = useAppSelector(
    (state: RootState) => state.AssessmentSlice.ongoingId
  );

  const videoProctoring = useAppSelector(
    (state) => state.AssessmentSlice.preAssessmentData?.videoProctoring
  );

  const browserProctoring = useAppSelector(
    (state) => state.AssessmentSlice.preAssessmentData?.browserProctoring
  );

  const tncEnabled = useAppSelector(
    (state) => state.AssessmentSlice.preAssessmentData?.tncEnabled
  );

  const isSubjective = useAppSelector(
    (state) =>
      state.AssessmentSlice.preAssessmentData?.questionTypeCountMap?.subjective
  );

  const user = useSelector((state: RootState) => state.UserInfoSlice);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm<FormValues>(
    user.profile.hasAssessmentRecOrgs
      ? {
          defaultValues: {
            email: user.profile.email,
            firstName: user.profile.name.firstName,
            lastName: user.profile.name.lastName
          }
        }
      : {
          defaultValues: {
            email: candidateDetails.email,
            firstName: candidateDetails.firstName,
            lastName: candidateDetails.lastName
          },
          resolver: yupResolver(validations)
        }
  );

  const formSubmitHandler = async (data: FormValues) => {
    const newUserDetails = {
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName
    };
    dispatch(AssessmentSliceActions.mutateCandidateDetails(newUserDetails));
    if (videoProctoring || tncEnabled || isSubjective > 0) {
      setActiveStep("next");
    } else {
      try {
        if (!scheduleId) {
          throw {
            status: "",
            title: "Schedule Id Not Found",
            err: {},
            description: ""
          };
        }

        const res = await startAssessment({
          scheduleId: scheduleId,
          tncAccepted: false,
          tncAcceptedAt: null
        });

        if (res.error) {
          throw {
            status: "",
            title: "Failed To Start The Assessment",
            err: res.error,
            description: ""
          };
        }

        const ongoingId = res?.data?.data?.id;

        if (ongoingId) {
          dispatch(AssessmentSliceActions.mutateOnGoingId(ongoingId));
        }
        const tabId = await generateUniqueId();
        if (tabId) {
          const body = {
            ongoingId: ongoingId,
            scheduleId: scheduleId,
            sessionId: tabId
          };
          const res1 = await updateTabId(body);
        }

        const proctoringStarted = await startProctoring();
        if (videoProctoring && !proctoringStarted) {
          throw {
            status: "",
            title: "Unable to start secure proctoring",
            err: {},
            description: "Please retry camera/proctoring setup and try again."
          };
        }
        dispatch(AssessmentSliceActions.mutateShowWarningModal(false));
        dispatch(AssessmentSliceActions.mutateAssessmentHasStarted(true));
        if (browserProctoring || videoProctoring) requestFullScreen(dispatch);
      } catch (err: unknown) {
        console.error(err);
        const temp = err as {
          title?: string;
          description?: string;
          err?: { data?: { error?: string[] } };
        };
        const rawErrList = temp?.err?.data?.error;
        const errorTitle = temp?.title ?? "Something went wrong";
        const errList: string[] = (rawErrList ?? []).map(
          (el: string) => el.split("_")[0]
        );
        if (showErrors)
          showErrors(errorTitle, errList, "error", temp?.description);
      }
    }
  };

  useEffect(() => {
    if (user?.profile?.email.endsWith("@globallogic.com")) {
      setUserInternalDetails(true);
      setValue("email", user?.profile?.email);
      setValue("firstName", user?.profile?.name?.firstName);
      setValue("lastName", user?.profile?.name?.lastName);
    }
  }, [user, setValue]);

  return (
    <form className="pt-10">
      <h1 className="text-2xl font-semibold">
        {t("assessment.stepper.personalDetails")}
      </h1>
      <div className="mt-10 space-y-10">
        <Input
          name="email"
          type="email"
          register={register}
          label={t("assessment.stepper.emailId")}
          error={Boolean(errors.email)}
          errorMessage={errors.email?.message}
          disabled={userInternalDetails || user.profile.hasAssessmentRecOrgs}
        />
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2">
          <Input
            name="firstName"
            type="text"
            register={register}
            label={t("assessment.stepper.firstName")}
            error={Boolean(errors.firstName)}
            errorMessage={errors.firstName?.message}
            disabled={userInternalDetails || user.profile.hasAssessmentRecOrgs}
          />
          <Input
            name="lastName"
            type="text"
            register={register}
            label={t("assessment.stepper.lastName")}
            error={Boolean(errors.lastName)}
            errorMessage={errors.lastName?.message}
            disabled={userInternalDetails || user.profile.hasAssessmentRecOrgs}
          />
        </div>
      </div>

      <div className="flex justify-end mt-10">
        <Button variant="contained" onClick={handleSubmit(formSubmitHandler)}>
          {!videoProctoring && !tncEnabled && isSubjective == 0
            ? t("assessment.stepper.startAssessment")
            : t("assessment.stepper.nextStep")}
        </Button>
      </div>
    </form>
  );
};

export default PersonalDetails;
